<template>
	<el-dialog :title="dialogTitle" :visible="visible" width="520px" append-to-body
		:close-on-click-modal="false" @close="onClose">
		<div class="kqd-body" v-loading="loading">
			<!-- 列表视图：我的每日客情记录 -->
			<div v-if="mode === 'list'">
				<div class="kqd-list-head">
					<span class="kqd-list-tip">客情每天统计一次，仅当天记录可修改</span>
					<el-button type="primary" size="small" icon="el-icon-plus"
						@click="openForm()">{{ todayRecord ? '修改今日客情' : '添加今日客情' }}</el-button>
				</div>
				<div v-if="!records.length" class="kqd-empty">
					暂无填报记录，点击右上方「添加今日客情」开始填报。
				</div>
				<table v-else class="kqd-table">
					<thead>
						<tr>
							<th>日期</th>
							<th>{{ totalFieldName }}</th>
							<th>填报时间</th>
							<th style="width:70px;">操作</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="r in records" :key="r.id">
							<td>
								{{ fmtDate(r.reportDate) }}
								<span v-if="isToday(r.reportDate)" class="kqd-tag-today">今天</span>
							</td>
							<td>{{ totalOf(r) }}</td>
							<td>{{ fmtTime(r.updatedTime || r.createdTime) }}</td>
							<td>
								<el-button v-if="isToday(r.reportDate)" type="text" size="mini"
									@click="openForm()">修改</el-button>
								<el-button v-else type="text" size="mini" @click="openView(r)">查看</el-button>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- 表单视图：填报/修改今日客情 -->
			<div v-else-if="mode === 'form'">
				<div class="kqd-meta">
					<div class="kqd-meta-item">
						<span class="kqd-meta-label">填报日期</span>
						<span class="kqd-meta-value">{{ reportDate }}</span>
					</div>
					<div class="kqd-meta-item" v-if="kqCtx && kqCtx.kqGroupName">
						<span class="kqd-meta-label">客勤群</span>
						<span class="kqd-meta-value">{{ kqCtx.kqGroupName }}</span>
					</div>
					<div class="kqd-meta-item">
						<span class="kqd-meta-label">填报时间</span>
						<span class="kqd-meta-value">{{ nowTime }}</span>
					</div>
				</div>
				<div v-if="!fields.length && !loading" class="kqd-empty">
					暂无可填报字段，请联系管理员在后台「客情配置」中启用字段。
				</div>
				<div v-else class="kqd-form">
					<div class="kqd-field" v-for="f in fields" :key="f.id">
						<div class="kqd-label">{{ f.name }}</div>
						<el-input v-model="content[String(f.id)]" size="small" maxlength="200"
							:placeholder="'请填写' + f.name"></el-input>
					</div>
					<div v-if="sumTip" class="kqd-sum-tip" :class="{ 'kqd-sum-err': !sumOk }">{{ sumTip }}</div>
				</div>
			</div>

			<!-- 查看视图：历史记录只读 -->
			<div v-else-if="mode === 'view'">
				<div class="kqd-meta">
					<div class="kqd-meta-item">
						<span class="kqd-meta-label">填报日期</span>
						<span class="kqd-meta-value">{{ viewRecord ? fmtDate(viewRecord.reportDate) : '' }}</span>
					</div>
					<div class="kqd-meta-item">
						<span class="kqd-meta-label">填报时间</span>
						<span class="kqd-meta-value">{{ viewRecord ? fmtTime(viewRecord.updatedTime || viewRecord.createdTime) : '' }}</span>
					</div>
				</div>
				<div class="kqd-form">
					<div class="kqd-field" v-for="f in fields" :key="f.id">
						<div class="kqd-label">{{ f.name }}</div>
						<div class="kqd-view-value">{{ viewContent[String(f.id)] || '-' }}</div>
					</div>
				</div>
				<div class="kqd-view-tip">历史记录仅可查看，不可修改。</div>
			</div>
		</div>
		<div slot="footer">
			<template v-if="mode === 'list'">
				<el-button size="small" @click="onClose">关闭</el-button>
			</template>
			<template v-else-if="mode === 'form'">
				<el-button size="small" @click="backToList">返回列表</el-button>
				<el-button type="primary" size="small" :loading="submitting"
					:disabled="!fields.length" @click="onSubmit">提交</el-button>
			</template>
			<template v-else>
				<el-button size="small" @click="backToList">返回列表</el-button>
			</template>
		</div>
	</el-dialog>
</template>

<script>
import bizApi from '../../api/bizApi.js';

export default {
	name: 'chatKqReportDialog',
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		// /biz/kq/group-check 返回的上下文 {kqGroupId, kqGroupName, canReport}
		kqCtx: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			mode: 'list', // list: 记录列表, form: 填报/修改今日, view: 查看历史
			records: [],
			viewRecord: null,
			reportDate: '',
			nowTime: '',
			fields: [],
			content: {},
			loading: false,
			submitting: false
		};
	},
	computed: {
		dialogTitle() {
			if (this.mode === 'form') {
				return this.todayRecord ? '修改今日客情' : '添加今日客情';
			}
			if (this.mode === 'view') {
				return '查看客情';
			}
			return '客情统计';
		},
		// 「总数」字段(名称包含总数)
		totalField() {
			return this.fields.find(f => f.name && f.name.indexOf('总数') >= 0) || null;
		},
		totalFieldName() {
			return this.totalField ? this.totalField.name : '总数';
		},
		todayRecord() {
			return this.records.find(r => this.isToday(r.reportDate)) || null;
		},
		viewContent() {
			return this.parseContent(this.viewRecord);
		},
		// 实时求和提示：总数应等于其余数值项之和
		sumInfo() {
			if (!this.totalField) return null;
			let sum = 0;
			this.fields.forEach(f => {
				if (f.id === this.totalField.id) return;
				const v = this.asNumber(this.content[String(f.id)]);
				if (v !== null) sum += v;
			});
			const total = this.asNumber(this.content[String(this.totalField.id)]);
			return { sum, total };
		},
		sumOk() {
			return this.sumInfo && this.sumInfo.total !== null && this.sumInfo.total === this.sumInfo.sum;
		},
		sumTip() {
			if (!this.sumInfo) return '';
			const { sum, total } = this.sumInfo;
			if (total === null) {
				return '各数值项合计：' + sum + '，请填写' + this.totalFieldName + '(数字)';
			}
			return this.totalFieldName + '：' + total + '，各项合计：' + sum + (total === sum ? ' ✓' : '（不一致）');
		}
	},
	methods: {
		pad(n) {
			return (n < 10 ? '0' : '') + n;
		},
		refreshDateTime() {
			const d = new Date();
			this.reportDate = d.getFullYear() + '-' + this.pad(d.getMonth() + 1) + '-' + this.pad(d.getDate());
			this.nowTime = this.reportDate + ' ' + this.pad(d.getHours()) + ':' + this.pad(d.getMinutes());
		},
		fmtDate(val) {
			if (!val) return '';
			const d = new Date(val);
			if (isNaN(d.getTime())) return String(val).slice(0, 10);
			return d.getFullYear() + '-' + this.pad(d.getMonth() + 1) + '-' + this.pad(d.getDate());
		},
		fmtTime(val) {
			if (!val) return '-';
			const d = new Date(val);
			if (isNaN(d.getTime())) return String(val).slice(0, 16);
			return this.fmtDate(d) + ' ' + this.pad(d.getHours()) + ':' + this.pad(d.getMinutes());
		},
		isToday(val) {
			return this.fmtDate(val) === this.reportDate;
		},
		asNumber(val) {
			if (val === null || val === undefined) return null;
			const s = String(val).trim();
			if (!s || !/^-?\d+$/.test(s)) return null;
			return parseInt(s, 10);
		},
		parseContent(record) {
			const map = {};
			if (record && record.content) {
				try {
					const obj = JSON.parse(record.content);
					Object.keys(obj || {}).forEach(k => { map[String(k)] = obj[k]; });
				} catch (e) { /* ignore */ }
			}
			return map;
		},
		totalOf(record) {
			if (!this.totalField) return '-';
			const c = this.parseContent(record);
			const v = c[String(this.totalField.id)];
			return (v === undefined || v === null || v === '') ? '-' : v;
		},
		async load() {
			this.refreshDateTime();
			this.mode = 'list';
			this.loading = true;
			try {
				this.fields = (await bizApi.kqFields()) || [];
				const data = await bizApi.kqReportHistory({ pageNo: 1, pageSize: 60 });
				this.records = (data && data.list) || [];
			} catch (e) {
				this.records = this.records || [];
			} finally {
				this.loading = false;
			}
		},
		openForm() {
			this.refreshDateTime();
			// 今天已填过则回显，支持修改后覆盖提交
			this.content = this.parseContent(this.todayRecord);
			this.mode = 'form';
		},
		openView(record) {
			this.viewRecord = record;
			this.mode = 'view';
		},
		backToList() {
			this.mode = 'list';
			this.viewRecord = null;
		},
		async onSubmit() {
			if (this.submitting) return;
			// 校验：总数 = 其余各数值项之和
			if (this.totalField && this.sumInfo) {
				const { sum, total } = this.sumInfo;
				if (total === null) {
					this.$message.error('请填写「' + this.totalFieldName + '」(数字)');
					return;
				}
				if (total !== sum) {
					this.$message.error('「' + this.totalFieldName + '」应等于其余各数值项之和：当前总数' + total + '，各项合计' + sum);
					return;
				}
			}
			this.submitting = true;
			try {
				const kqGroupId = this.kqCtx ? this.kqCtx.kqGroupId : null;
				await bizApi.kqReportSubmit(this.reportDate, this.content, kqGroupId);
				this.$message.success('客情填报提交成功');
				await this.load();
			} catch (e) {
				// http 拦截器已弹出错误提示
			} finally {
				this.submitting = false;
			}
		},
		onClose() {
			this.$emit('close');
		}
	},
	watch: {
		visible(val) {
			if (val) {
				this.load();
			}
		}
	}
};
</script>

<style scoped lang="scss">
.kqd-body {
	max-height: 55vh;
	overflow-y: auto;
	text-align: left;
}

.kqd-list-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;

	.kqd-list-tip {
		font-size: 12px;
		color: var(--im-text-color-light, #909399);
	}
}

.kqd-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;

	th,
	td {
		padding: 8px 10px;
		border-bottom: 1px solid rgba(144, 147, 153, 0.18);
		text-align: left;
	}

	th {
		color: var(--im-text-color-light, #909399);
		font-weight: 500;
	}
}

.kqd-tag-today {
	display: inline-block;
	margin-left: 6px;
	padding: 0 6px;
	font-size: 11px;
	line-height: 18px;
	border-radius: 9px;
	color: #fff;
	background: var(--im-color-primary, #409eff);
}

.kqd-meta {
	display: flex;
	flex-wrap: wrap;
	gap: 6px 18px;
	padding: 10px 12px;
	margin-bottom: 14px;
	border-radius: 8px;
	background: rgba(64, 158, 255, 0.08);

	.kqd-meta-item {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 13px;
	}

	.kqd-meta-label {
		color: var(--im-text-color-light, #909399);
	}

	.kqd-meta-value {
		font-weight: 600;
		color: var(--im-text-color, #303133);
	}
}

.kqd-empty {
	padding: 30px 0;
	text-align: center;
	color: var(--im-text-color-light, #909399);
	font-size: 13px;
}

.kqd-form {
	.kqd-field {
		margin-bottom: 12px;

		.kqd-label {
			font-size: 13px;
			margin-bottom: 5px;
			color: var(--im-text-color-light, #606266);
		}
	}
}

.kqd-sum-tip {
	margin-top: 4px;
	padding: 6px 10px;
	font-size: 12px;
	border-radius: 6px;
	color: #67c23a;
	background: rgba(103, 194, 58, 0.1);

	&.kqd-sum-err {
		color: #f56c6c;
		background: rgba(245, 108, 108, 0.1);
	}
}

.kqd-view-value {
	padding: 7px 10px;
	font-size: 13px;
	border-radius: 6px;
	background: rgba(144, 147, 153, 0.1);
	color: var(--im-text-color, #303133);
	min-height: 18px;
	word-break: break-all;
}

.kqd-view-tip {
	margin-top: 10px;
	font-size: 12px;
	text-align: center;
	color: var(--im-text-color-light, #909399);
}
</style>
